// DOM builders for <vf-income-explorer> levels 3 and 4: identifier cells
// with copy controls, the payer-grouped stored-transaction table, and the
// single-transaction decode view. Pure DOM factories — no fetching and no
// component state; callbacks wire navigation back to the element.
//
// Identifier law: payer pubkeys, transaction signatures, program ids, and
// balance-delta accounts always render in full (monospace, wrapping) with a
// copy control beside each — never truncated here.
//
// Null law: an absent served value renders as its written absence string
// (income-explorer.ts constants), never 0 and never a dash.

import { formatCount, formatUtcDateTime, lamportsToSol } from "./format";
import {
  BLOCK_TIME_NOT_SERVED_TEXT,
  decodedInstructionText,
  NO_BALANCE_CHANGES_TEXT,
  NO_LOGS_TEXT,
  NO_REGISTRY_LABEL_TEXT,
  NOT_SERVED_TEXT,
  rawDataFactsText,
  type BlockTxRowUi,
  type InstructionUi,
  type PayerGroup,
  type TxDetailData,
} from "./income-explorer";

const COPY_RESET_MS = 1200;
const COPY_FAIL_RESET_MS = 1600;

/** Rendered success state; [✓]/[✗] are the two permitted boolean-state symbols. */
export function successText(success: boolean): string {
  return success ? "success [✓]" : "failed [✗]";
}

/** Signed exact SOL for balance deltas: an explicit "+" on gains, "-" comes from the formatter. */
export function signedSol(lamports: number): string {
  return lamports > 0 ? `+${lamportsToSol(lamports)}` : lamportsToSol(lamports);
}

/** Copy control beside an identifier. Never triggers the enclosing row's action. */
export function copyControl(value: string, subject: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "vf-btn vf-copy";
  btn.textContent = "copy";
  btn.setAttribute("aria-label", `copy ${subject}`);
  // "copied"/"copy failed" are textContent swaps — announced only with
  // live-region semantics on the button itself.
  btn.setAttribute("aria-live", "polite");
  btn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    // Both failure modes — clipboard unavailable (non-secure context) and a
    // rejected write (permission denied) — reach the visible "copy failed"
    // state; a button that looks live and silently does nothing is not an
    // affordance. The full identifier stays selectable text either way.
    const failed = (): void => {
      btn.textContent = "copy failed";
      setTimeout(() => {
        btn.textContent = "copy";
      }, COPY_FAIL_RESET_MS);
    };
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (clipboard === undefined) {
      failed();
      return;
    }
    clipboard
      .writeText(value)
      .then(() => {
        btn.textContent = "copied";
        setTimeout(() => {
          btn.textContent = "copy";
        }, COPY_RESET_MS);
      })
      .catch(failed);
  });
  return btn;
}

/** Full identifier (monospace, wrapping) with its copy control. */
export function identifierWithCopy(value: string, subject: string): HTMLSpanElement {
  const wrap = document.createElement("span");
  wrap.className = "vf-ident-wrap";
  const ident = document.createElement("span");
  ident.className = "vf-mono vf-ident";
  ident.textContent = value;
  wrap.append(ident, copyControl(value, subject));
  return wrap;
}

/** `numeric` marks a pure-numeric column cell (vf-num right-aligns it); mixed prose/result cells stay left even when mono. */
function td(content: string | Node, mono = false, numeric = false): HTMLTableCellElement {
  const cell = document.createElement("td");
  if (mono) {
    cell.classList.add("vf-mono");
  }
  if (numeric) {
    cell.classList.add("vf-num");
  }
  if (typeof content === "string") {
    cell.textContent = content;
  } else {
    cell.append(content);
  }
  return cell;
}

/** Headers follow their columns: `numeric[i]` right-aligns the i-th header with its vf-num column. */
function headerRow(
  labels: readonly string[],
  numeric: readonly boolean[] = [],
): HTMLTableSectionElement {
  const thead = document.createElement("thead");
  const tr = document.createElement("tr");
  labels.forEach((label, i) => {
    const th = document.createElement("th");
    th.textContent = label;
    if (numeric[i] === true) {
      th.classList.add("vf-num");
    }
    tr.append(th);
  });
  thead.append(tr);
  return thead;
}

/** Button semantics for a clickable table row: keyboard reachable, Enter/Space activate. Shared with the blocks datastream panel. */
export function rowButtonSemantics(tr: HTMLTableRowElement, activate: () => void): void {
  tr.tabIndex = 0;
  tr.setAttribute("role", "button");
  tr.addEventListener("click", activate);
  tr.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      activate();
    }
  });
}

export interface PayerTableCallbacks {
  onTogglePayer: (payer: string) => void;
  onTxSelect: (row: BlockTxRowUi) => void;
}

function txRowsTable(rows: readonly BlockTxRowUi[], cb: PayerTableCallbacks): HTMLElement {
  // The block store keeps a top subset of the block's transactions, so a
  // payer ranked by the full-block summary can expand to zero stored
  // rows — that absence is stated in writing (null law), never rendered
  // as a bare header row over nothing.
  if (rows.length === 0) {
    return absenceNote("this payer's transactions are not among the stored transactions for this block");
  }
  const table = document.createElement("table");
  table.className = "vf-tx-inner";
  table.append(
    headerRow(["signature", "fee (SOL)", "tip (SOL)", "CU", "result"], [false, true, true, true, false]),
  );
  const tbody = document.createElement("tbody");
  for (const row of rows) {
    const tr = document.createElement("tr");
    tr.className = "vf-tx-row";
    tr.setAttribute("aria-label", `decode transaction ${row.signature}`);
    tr.append(
      td(identifierWithCopy(row.signature, "transaction signature")),
      td(lamportsToSol(row.feeLamports), true, true),
      td(lamportsToSol(row.tipLamports), true, true),
      td(row.computeUnits === null ? NOT_SERVED_TEXT : formatCount(row.computeUnits), true, true),
      td(successText(row.success), true),
    );
    rowButtonSemantics(tr, () => cb.onTxSelect(row));
    tbody.append(tr);
  }
  table.append(tbody);
  return table;
}

/**
 * Level-3 table: one row per payer (full pubkey + copy, tx count, summed
 * fees+tips in SOL), expandable to the payer's stored transaction rows.
 * Groups arrive already ordered (income-explorer.ts groupByPayer).
 */
export function renderPayerTable(
  groups: readonly PayerGroup[],
  expanded: ReadonlySet<string>,
  cb: PayerTableCallbacks,
): HTMLTableElement {
  const table = document.createElement("table");
  table.className = "vf-payer-table";
  table.append(headerRow(["payer", "txs", "fees + tips (SOL)"], [false, true, true]));
  const tbody = document.createElement("tbody");
  for (const group of groups) {
    const isOpen = expanded.has(group.payer);
    const tr = document.createElement("tr");
    tr.className = "vf-payer-row";
    tr.setAttribute("aria-expanded", String(isOpen));
    tr.setAttribute("aria-label", `payer ${group.payer}: toggle transactions`);
    tr.append(
      td(identifierWithCopy(group.payer, "payer pubkey")),
      td(formatCount(group.txCount), true, true),
      td(lamportsToSol(group.feesPlusTipsLamports), true, true),
    );
    rowButtonSemantics(tr, () => cb.onTogglePayer(group.payer));
    tbody.append(tr);
    if (isOpen) {
      const detailTr = document.createElement("tr");
      detailTr.className = "vf-payer-detail";
      const cell = document.createElement("td");
      cell.colSpan = 3;
      cell.append(txRowsTable(group.rows, cb));
      detailTr.append(cell);
      tbody.append(detailTr);
    }
  }
  table.append(tbody);
  return table;
}

function sectionLabel(text: string): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "vf-explorer-section-label";
  el.textContent = text;
  return el;
}

function absenceNote(text: string): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "vf-absence";
  el.textContent = text;
  return el;
}

function factsTable(rows: readonly [string, string | Node][]): HTMLTableElement {
  const table = document.createElement("table");
  const tbody = document.createElement("tbody");
  for (const [label, value] of rows) {
    const tr = document.createElement("tr");
    const labelCell = document.createElement("td");
    labelCell.textContent = label;
    labelCell.className = "vf-fact-label";
    tr.append(labelCell, td(value, typeof value === "string"));
    tbody.append(tr);
  }
  table.append(tbody);
  return table;
}

/**
 * Per-instruction decode detail (v0.11.1 API fields), or null when the
 * instruction carries none of them (pre-0.11.1 daemon, or a node-parsed
 * instruction whose info the node did not return).
 *
 * Three sources, each labeled and rendered without interpretation:
 * - `decoded` — the API's spec-fixed ComputeBudget decode, in words with
 *   exact counts (income-explorer.ts decodedInstructionText);
 * - `parsedInfo` — the RPC node's own decode, verbatim pretty-printed JSON
 *   in a bounded scroll block (an spl-memo string renders as that string);
 * - `data` — raw base58 in full with a copy control (identifier law), plus
 *   byte length and discriminator facts.
 */
function instructionDetail(ins: InstructionUi): HTMLDivElement | null {
  if (ins.decoded === null && ins.parsedInfo === null && ins.data === null) {
    return null;
  }
  const wrap = document.createElement("div");
  wrap.className = "vf-ins-detail-body";

  if (ins.decoded !== null) {
    const line = document.createElement("div");
    line.className = "vf-ins-decoded vf-mono";
    line.textContent = decodedInstructionText(ins.decoded);
    wrap.append(line);
  }

  if (ins.parsedInfo !== null) {
    const info = document.createElement("div");
    info.className = "vf-log vf-mono vf-ins-parsed-info";
    info.textContent =
      typeof ins.parsedInfo === "string" ? ins.parsedInfo : JSON.stringify(ins.parsedInfo, null, 2);
    wrap.append(info);
  }

  if (ins.data !== null) {
    const facts = document.createElement("div");
    facts.className = "vf-ins-data-facts vf-mono";
    facts.textContent = rawDataFactsText(ins.data);
    wrap.append(identifierWithCopy(ins.data.base58, "instruction data"), facts);
  }

  return wrap;
}

/**
 * Level-4 view: full-signature header, decode facts, nonzero balance deltas
 * (delta in exact signed SOL), labeled instruction list, and the served log
 * tail in its own scroll container.
 *
 * `tipLamports` is the served tip of the level-3 row that was clicked;
 * /tx/{signature} itself serves no tip figure, so its source is labeled.
 */
export function renderTxDetail(d: TxDetailData, tipLamports: number): HTMLDivElement {
  const root = document.createElement("div");
  root.className = "vf-tx-detail";

  root.append(sectionLabel("signature"));
  root.append(identifierWithCopy(d.signature, "transaction signature"));

  root.append(
    factsTable([
      ["result", successText(d.success)],
      ["slot", formatCount(d.slot)],
      ["block time", d.blockTime === null ? BLOCK_TIME_NOT_SERVED_TEXT : formatUtcDateTime(d.blockTime)],
      ["payer", identifierWithCopy(d.payer, "payer pubkey")],
      ["fee", `${lamportsToSol(d.feeLamports)} SOL`],
      ["tip (from block index)", `${lamportsToSol(tipLamports)} SOL`],
      ["compute units", d.computeUnits === null ? NOT_SERVED_TEXT : formatCount(d.computeUnits)],
    ]),
  );

  root.append(sectionLabel("balance changes (nonzero only)"));
  if (d.balanceChanges.length === 0) {
    root.append(absenceNote(NO_BALANCE_CHANGES_TEXT));
  } else {
    const table = document.createElement("table");
    table.append(headerRow(["account", "delta (SOL)"], [false, true]));
    const tbody = document.createElement("tbody");
    for (const change of d.balanceChanges) {
      const tr = document.createElement("tr");
      tr.append(
        td(identifierWithCopy(change.account, "account pubkey")),
        td(signedSol(change.deltaLamports), true, true),
      );
      tbody.append(tr);
    }
    table.append(tbody);
    root.append(table);
  }

  root.append(sectionLabel("instructions"));
  if (d.instructions.length === 0) {
    root.append(absenceNote("no instructions decoded"));
  } else {
    const table = document.createElement("table");
    table.append(headerRow(["program", "label"]));
    const tbody = document.createElement("tbody");
    for (const ins of d.instructions) {
      const tr = document.createElement("tr");
      const labelCell = document.createElement("td");
      labelCell.textContent = ins.label ?? NO_REGISTRY_LABEL_TEXT;
      if (ins.parsedType !== null) {
        const typeSpan = document.createElement("span");
        typeSpan.className = "vf-parsed-type vf-mono";
        typeSpan.textContent = ` · ${ins.parsedType}`;
        labelCell.append(typeSpan);
      }
      tr.append(td(identifierWithCopy(ins.programId, "program id")), labelCell);
      tbody.append(tr);
      const detail = instructionDetail(ins);
      if (detail !== null) {
        const detailTr = document.createElement("tr");
        detailTr.className = "vf-ins-detail";
        const cell = document.createElement("td");
        cell.colSpan = 2;
        cell.append(detail);
        detailTr.append(cell);
        tbody.append(detailTr);
      }
    }
    table.append(tbody);
    root.append(table);
  }

  const lineNoun = d.logMessagesTail.length === 1 ? "line" : "lines";
  root.append(sectionLabel(`log tail (last ${d.logMessagesTail.length} ${lineNoun} served)`));
  if (d.logMessagesTail.length === 0) {
    root.append(absenceNote(NO_LOGS_TEXT));
  } else {
    const log = document.createElement("div");
    log.className = "vf-log vf-mono";
    log.textContent = d.logMessagesTail.join("\n");
    root.append(log);
  }

  return root;
}
